import type { DailyHistoryCell, DailyStatus } from './EntityDailyHistoryPanel.types';

type DailyHistoryDetail = DailyHistoryCell['details'][number];

type BuildLatestDailyHistoryArgs = {
  timestamp: Date | null | undefined;
  status: Exclude<DailyStatus, null>;
  detail: DailyHistoryDetail;
};

export const buildLatestDailyHistory = ({
  timestamp,
  status,
  detail,
}: BuildLatestDailyHistoryArgs): DailyHistoryCell[] => {
  if (!timestamp) {
    return [];
  }

  return [
    {
      day: timestamp,
      status,
      statuses: [status],
      details: [detail],
    },
  ];
};

type BuildDailyHistoryFromDetailsArgs<TStatus extends Exclude<DailyStatus, null>> = {
  items: readonly DailyHistoryDetail[];
  getDay: (item: DailyHistoryDetail) => Date;
  getStatus: (item: DailyHistoryDetail) => TStatus;
};

export const buildDailyHistoryFromDetails = <TStatus extends Exclude<DailyStatus, null>>({
  items,
  getDay,
  getStatus,
}: BuildDailyHistoryFromDetailsArgs<TStatus>): DailyHistoryCell[] =>
  Array.from(
    items
      .reduce<Map<string, DailyHistoryCell>>((acc, item) => {
        const day = getDay(item);
        const dayKey = day.toISOString().slice(0, 10);
        const existing = acc.get(dayKey) ?? {
          day: new Date(`${dayKey}T00:00:00.000Z`),
          status: null,
          statuses: [],
          details: [],
        };
        existing.statuses.push(getStatus(item));
        existing.details.push(item);
        existing.details.sort(
          (left, right) => right.timestamp.getTime() - left.timestamp.getTime(),
        );
        existing.status = existing.details[0]?.status ?? null;
        acc.set(dayKey, existing);
        return acc;
      }, new Map())
      .values(),
  ).sort((left, right) => right.day.getTime() - left.day.getTime());

export const buildDailyHistoryForReferenceDays = <TDetail extends DailyHistoryDetail>(
  referenceHistory: DailyHistoryCell[],
  detailsByDayKey: ReadonlyMap<string, TDetail[]>,
): DailyHistoryCell[] =>
  referenceHistory.map((historyDay) => {
    const dayKey = historyDay.day.toISOString().slice(0, 10);
    const details = detailsByDayKey.get(dayKey) ?? [];
    const statuses = details.map((detail) => detail.status);

    return {
      day: historyDay.day,
      status: statuses[0] ?? null,
      statuses,
      details,
    };
  });
